import React from "react";

import { SLIDESHOW } from "../../data/content";

const INTERVAL_MS = 6000;

const Arrow = ({ isNext }) => (
  <svg
    viewBox={"0 0 21 41"}
    aria-hidden={"true"}
  >
    <path d={isNext ? "M.7.2 21 20.5.7 40.8 0 40.1 19.7 20.5 0 .9z" : "M20.3 40.8 0 20.5 20.3.2l.7.7L1.3 20.5 21 40.1z"} />
  </svg>
);

//Full-width photo slideshow behind the home page heading. Moves on by itself (as on Wix), pausing while the visitor
//hovers or uses the controls, and not at all for visitors who prefer reduced motion. Dots and swiping added
const Slideshow = ({ children }) => {
  const [current, setCurrent] = React.useState(0);
  const [loadUpTo, setLoadUpTo] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const touchStart = React.useRef(null);
  const count = SLIDESHOW.length;

  //Photos load one ahead of the one showing, so the first page load stays light
  React.useEffect(() => {
    const timeout = window.setTimeout(() => setLoadUpTo((n) => Math.max(n, current + 1)), current === 0 ? 1500 : 0);
    return () => window.clearTimeout(timeout);
  }, [current]);

  React.useEffect(() => {
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isPaused || reduceMotion) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCurrent((c) => (c + 1) % count), INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [current, isPaused, count]);

  const go = (step) => {
    setLoadUpTo(count);
    setCurrent((c) => (c + step + count) % count);
  };

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) {
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (Math.abs(dx) > 50) {
      go(dx < 0 ? 1 : -1);
    }
  };

  return (
    <section
      className={"home-hero"}
      aria-roledescription={"carousel"}
      aria-label={"Estia venue photos"}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <ul className={"home-hero-slides"}>
        {SLIDESHOW.map((slide, i) => (
          <li
            key={slide.src}
            className={`home-hero-slide ${i === current ? "is-current" : ""}`}
            aria-roledescription={"slide"}
            aria-label={`${i + 1} of ${count}`}
            aria-hidden={i !== current}
          >
            {i <= loadUpTo ? (
              <img
                src={slide.src}
                alt={slide.alt}
                width={slide.width}
                height={slide.height}

              />
            ) : null}
          </li>
        ))}
      </ul>

      <div className={"home-hero-content"}>{children}</div>

      <button
        type={"button"}
        className={"home-hero-arrow is-prev"}
        aria-label={"Previous photo"}
        onClick={() => go(-1)}
      >
        <Arrow />
      </button>
      <button
        type={"button"}
        className={"home-hero-arrow is-next"}
        aria-label={"Next photo"}
        onClick={() => go(1)}
      >
        <Arrow isNext />
      </button>

      <div className={"home-hero-dots"}>
        {SLIDESHOW.map((slide, i) => (
          <button
            key={slide.src}
            type={"button"}
            className={`home-hero-dot ${i === current ? "is-current" : ""}`}
            aria-label={`Show photo ${i + 1}`}
            aria-current={i === current ? "true" : undefined}
            onClick={() => {
              setLoadUpTo(count);
              setCurrent(i);
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Slideshow;
